import React, { useState, useEffect, useRef, useMemo } from "react";
import CameraCapture from "../../Components/ProfileCameraCapture/CameraCapture";
import FingerprintCapture from "../../Components/FingerprintCapture/FingerprintCapture";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import Toastify from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import { compressImageFile } from "../../utils/compressImage";
import { deleteFingerprint, enrollFingerprint } from "../../utils/mantraFingerprint";
import SingleDatePicker from "../../Components/SingleDatePicker/SingleDatePicker";
import { MdOutlineCalendarToday } from "react-icons/md";
import UploadIcon from "../../assets/image/upload.svg";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API || "https://backend.mipl.quytech.net/v1";

/** Order matches API `document_type` values. */
const ID_PROOF_SLOTS = [
  { key: "driver_license", label: "Driver license (front)", uploadName: "driver license front" },
  { key: "driver_license_back", label: "Driver license (back)", uploadName: "driver license back" },
  { key: "adhar_card_front", label: "Aadhaar card (front)", uploadName: "Aadhaar front" },
  { key: "adhar_card_back", label: "Aadhaar card (back)", uploadName: "Aadhaar back" },
];

const missingDocumentsMessage = (missing) => {
  const names = missing.map((s) => s.uploadName);
  if (names.length === 0) return "";
  if (names.length === 1) return `Upload ${names[0]} image.`;
  const last = names[names.length - 1];
  const rest = names.slice(0, -1).join(", ");
  return `Upload ${rest}, and ${last} images.`;
};

const emptyIdProofs = () =>
  Object.fromEntries(ID_PROOF_SLOTS.map(({ key }) => [key, null]));

/** Applied only when submitting — selected files stay uncompressed until then. */
const SUBMIT_COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.82,
  outputMimeType: "image/jpeg",
};

const isImageForSubmitCompression = (f) => {
  const t = f.type || "";
  if (t.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(f.name || "");
};

const formatFileSize = (bytes) => {
  if (bytes == null || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * @param {{ label: string; beforeBytes: number; afterBytes: number; kind: string }} entry
 */
const formatCompressionLogLine = (entry) => {
  const { label, beforeBytes, afterBytes, kind } = entry;
  const b = formatFileSize(beforeBytes);
  const a = formatFileSize(afterBytes);
  if (kind === "fallback") {
    return `${label}: compression failed, using original · ${b}`;
  }
  if (kind === "skipped") return `${label}: ${b} (unchanged)`;
  const pct =
    beforeBytes > 0 ? Math.round((1 - afterBytes / beforeBytes) * 100) : 0;
  return `${label}: ${b} → ${a} (${pct}% smaller)`;
};

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

const digitsOnly = (s) => String(s || "").replace(/\D/g, "");

const FULL_NAME_MAX_LENGTH = 100;

const normalizeFullNameInput = (raw) =>
  String(raw ?? "")
    .replace(/^\s+/, "")
    .replace(/[^A-Za-z ]/g, "")
    .replace(/\s{2,}/g, " ")
    .slice(0, FULL_NAME_MAX_LENGTH);

const isValidFullName = (value) => {
  const name = String(value || "").trim();
  return /^[A-Za-z]+(?: [A-Za-z]+)*$/.test(name);
};

const LICENSE_MAX_LENGTH = 15;
const INDIAN_LICENSE_PATTERN = /^[A-Z]{2}\d{13}$/;

const normalizeLicenseInput = (raw) =>
  String(raw ?? "")
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase()
    .slice(0, LICENSE_MAX_LENGTH);

const isValidLicenseNumber = (value) => {
  const license = normalizeLicenseInput(value);
  return INDIAN_LICENSE_PATTERN.test(license);
};

const INDIAN_MOBILE_PATTERN = /^[6-9]\d{9}$/;

const normalizeAadhaarForApi = (value) => {
  const d = digitsOnly(value);
  if (d.length === 12) {
    return `${d.slice(0, 4)} ${d.slice(4, 8)} ${d.slice(8, 12)}`;
  }
  return (value || "").trim();
};

const formatAadhaarInput = (value) => {
  const d = digitsOnly(value).slice(0, 12);
  if (!d) return "";
  const parts = d.match(/.{1,4}/g) || [];
  return parts.join(" ");
};

const getTodayDateString = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDateYearsAgoString = (years) => {
  const now = new Date();
  now.setFullYear(now.getFullYear() - years);
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const OTP_LEN = 6;
const OTP_TIMEOUT_SEC = 90;

const emptyOtpDigits = () => Array(OTP_LEN).fill("");

const datePickerTriggerIcon = (
  <MdOutlineCalendarToday
    className="ml-2 h-4 w-4 shrink-0 text-gray-600"
    aria-hidden
  />
);

const FieldLabel = ({ htmlFor, children }) => (
  <label
    htmlFor={htmlFor}
    className="mb-1 block text-sm font-UbuntuMedium text-gray-700"
  >
    {children}
    <span className="text-danger"> *</span>
  </label>
);

const parseMobile = (raw) => {
  const digits = digitsOnly(raw);
  if (digits.length === 12 && digits.startsWith("91")) {
    return { country_code: "+91", mobile_number: digits.slice(2, 12) };
  }
  if (digits.length === 10) {
    return { country_code: "+91", mobile_number: digits };
  }
  return { country_code: "+91", mobile_number: digits };
};

const extractProfileLocation = (payload) => {
  const profile = payload?.data ?? payload?.result ?? payload?.payload ?? payload;
  const location = profile?.mipl_location_id;

  if (location && typeof location === "object") {
    const id = location._id ?? location.id;
    const label = location.name ?? location.location_name ?? location.title ?? id;
    return {
      id: id != null ? String(id) : "",
      label: label != null ? String(label) : "",
    };
  }

  if (location != null && String(location).trim() !== "") {
    const id = String(location).trim();
    return { id, label: profile?.mipl_location_name ? String(profile.mipl_location_name) : id };
  }

  return { id: "", label: "" };
};

const AddNewDriver = () => {
  const navigate = useNavigate();
  const { setSubheading } = useOutletContext();
  const maxLicenseCreationDate = getDateYearsAgoString(1);

  useEffect(() => {
    setSubheading("Add Driver Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  const [form, setForm] = useState({
    name: "",
    license: "",
    license_creation_date: "",
    expiry: "",
    nt_expiry: "",
    aadhaar: "",
    phone: "",
    age: "",
    mipl_location_id: "",
    type:"Driver Verification"
  });

  const [profileImage, setProfileImage] = useState(null);
  const [idProofs, setIdProofs] = useState(emptyIdProofs);
  const [fingerprintSignature, setFingerprintSignature] = useState("");

  const canVerifyFingerprint = useMemo(() => {
    const filled = (value) => String(value || "").trim().length > 0;
    return (
      Boolean(profileImage) &&
      filled(form.name) &&
      filled(form.license) &&
      filled(form.license_creation_date) &&
      filled(form.expiry) &&
      filled(form.nt_expiry) &&
      filled(form.aadhaar) &&
      filled(form.phone) &&
      filled(form.age) &&
      ID_PROOF_SLOTS.every(({ key }) => Boolean(idProofs[key]))
    );
  }, [form, profileImage, idProofs]);

  const idProofPreviewUrls = useMemo(() => {
    const urls = {};
    ID_PROOF_SLOTS.forEach(({ key }) => {
      const file = idProofs[key];
      urls[key] = file ? URL.createObjectURL(file) : null;
    });
    return urls;
  }, [idProofs]);

  useEffect(() => {
    return () => {
      Object.values(idProofPreviewUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [idProofPreviewUrls]);

  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState(emptyOtpDigits);
  /** Shown in modal when API returns `data.otp` (e.g. dev / SMS not wired). */
  const [otpFromResponse, setOtpFromResponse] = useState("");
  const [timeLeft, setTimeLeft] = useState(OTP_TIMEOUT_SEC);
  /** Set true when user clicks Add (valid form); OTP verify reads this before clearing. */
  const pendingDriverSubmitRef = useRef(false);
  const prevPhoneForOtpRef = useRef(null);
  /** True after enroll succeeds and until addDriver succeeds or we delete it. */
  const fingerprintEnrolledRef = useRef(false);
  const enrolledExternalIdRef = useRef("");
  const driverCreatedRef = useRef(false);
  const [otpSendLoading, setOtpSendLoading] = useState(false);
  const [otpVerifyLoading, setOtpVerifyLoading] = useState(false);
  const otpRefs = useRef([]);

  const getFingerprintExternalId = () =>
    String(
      normalizeAadhaarForApi(form.aadhaar) ||
        parseMobile(form.phone).mobile_number ||
        ""
    ).trim();

  const discardEnrolledFingerprint = async () => {
    if (driverCreatedRef.current) return;
    const externalId = enrolledExternalIdRef.current;
    if (!fingerprintEnrolledRef.current || !externalId) return;
    fingerprintEnrolledRef.current = false;
    enrolledExternalIdRef.current = "";
    try {
      await deleteFingerprint({ externalId });
    } catch (err) {
      console.error(err);
    }
  };

  const closeOtpModal = () => {
    void discardEnrolledFingerprint();
    setShowOTP(false);
    setOtpFromResponse("");
    pendingDriverSubmitRef.current = false;
  };

  const handleBackToDriverList = (e) => {
    e.preventDefault();
    void (async () => {
      await discardEnrolledFingerprint();
      navigate("/driver-management");
    })();
  };

  const [loading, setLoading] = useState(false);
  /** @type {'idle' | 'enroll' | 'compress' | 'upload'} */
  const [submitStage, setSubmitStage] = useState("idle");
  /** @type {Array<{ label: string; beforeBytes: number; afterBytes: number; kind: string }> | null} */
  const [compressionLogs, setCompressionLogs] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [profileLocationLoading, setProfileLocationLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadProfileLocation = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        setProfileLocationLoading(false);
        return;
      }

      setProfileLocationLoading(true);
      const url = `${ACCOUNTANT_API}${endpoint.getAdminProfile}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await parseBody(response);

        if (!response.ok) {
          if (!cancelled) {
            toast.error(getErrorMessage(payload, response));
          }
          return;
        }

        const apiCode = payload?.code;
        if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
          if (!cancelled) {
            toast.error(getErrorMessage(payload, response));
          }
          return;
        }

        const location = extractProfileLocation(payload);
        if (!cancelled) {
          setForm((prev) => ({ ...prev, mipl_location_id: location.id }));
          if (location.id) {
            setFieldErrors((prev) => {
              if (!prev.mipl_location_id) return prev;
              const next = { ...prev };
              delete next.mipl_location_id;
              return next;
            });
          }
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Could not load profile location. Check your connection."
              : "Could not load profile location."
          );
        }
      } finally {
        if (!cancelled) setProfileLocationLoading(false);
      }
    };

    loadProfileLocation();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!showOTP) return;
    if (timeLeft === 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [showOTP, timeLeft]);

  useEffect(() => {
    if (!showOTP) return undefined;
    const id = requestAnimationFrame(() => {
      otpRefs.current[0]?.focus?.();
    });
    return () => cancelAnimationFrame(id);
  }, [showOTP]);

  useEffect(() => {
    const next = digitsOnly(form.phone);
    if (prevPhoneForOtpRef.current === null) {
      prevPhoneForOtpRef.current = next;
      return;
    }
    if (prevPhoneForOtpRef.current === next) return;

    const modalWasOpen = showOTP;
    prevPhoneForOtpRef.current = next;

    if (!modalWasOpen) {
      pendingDriverSubmitRef.current = false;
      return;
    }

    setOtp(emptyOtpDigits());
    setOtpFromResponse("");
    pendingDriverSubmitRef.current = false;
    setShowOTP(false);

    if (INDIAN_MOBILE_PATTERN.test(next)) {
      toast.info("Phone updated. Sending a new OTP…");
      pendingDriverSubmitRef.current = true;
      void (async () => {
        const ok = await requestSendOtp();
        if (ok) {
          setTimeLeft(OTP_TIMEOUT_SEC);
          setShowOTP(true);
        } else {
          pendingDriverSubmitRef.current = false;
        }
      })();
    } else {
      toast.info("Enter a valid 10-digit number, then tap Add again to receive an OTP.");
    }
    // requestSendOtp is stable enough for this flow; including it would retrigger too often.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to phone / modal visibility
  }, [form.phone, showOTP]);

  useEffect(() => {
    const enrolledId = enrolledExternalIdRef.current;
    if (!fingerprintEnrolledRef.current || !enrolledId) return;
    const currentId = getFingerprintExternalId();
    if (currentId && currentId !== enrolledId) {
      void discardEnrolledFingerprint();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.aadhaar, form.phone]);

  useEffect(
    () => () => {
      if (driverCreatedRef.current) return;
      const externalId = enrolledExternalIdRef.current;
      if (!fingerprintEnrolledRef.current || !externalId) return;
      fingerprintEnrolledRef.current = false;
      enrolledExternalIdRef.current = "";
      void deleteFingerprint({ externalId }).catch((err) => {
        console.error(err);
      });
    },
    []
  );

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const next = digitsOnly(value).slice(0, 10);
      if (next.length > 0 && !/^[6-9]/.test(next)) return;
      setForm({ ...form, [name]: next });
    } else if (name === "name") {
      setForm({ ...form, [name]: normalizeFullNameInput(value) });
    } else if (name === "license") {
      setForm({ ...form, [name]: normalizeLicenseInput(value) });
    } else if (name === "aadhaar") {
      setForm({ ...form, [name]: formatAadhaarInput(value) });
    } else if (name === "age") {
      setForm({ ...form, [name]: digitsOnly(value).slice(0, 3) });
    } else {
      setForm({ ...form, [name]: value });
    }
    if (fieldErrors[name]) clearFieldError(name);
  };

  const handleProfileCapture = (file) => {
    setProfileImage(file);
    clearFieldError("profileImage");
  };

  const handleIdProofUpload = (slotKey) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG or PNG images allowed for ID proofs.");
      e.target.value = null;
      return;
    }

    setIdProofs((prev) => ({ ...prev, [slotKey]: file }));
    clearFieldError("documents");
    e.target.value = null;
  };

  const removeIdProof = (slotKey) => {
    setIdProofs((prev) => ({ ...prev, [slotKey]: null }));
  };

  const validate = () => {
    const next = {};

    const name = form.name.trim();
    if (!name) next.name = "Full name is required.";
    else if (name.length < 2) next.name = "Please enter a valid full name.";
    else if (!isValidFullName(name)) {
      next.name = "Full name must contain only letters.";
    }

    if (!form.license.trim()) next.license = "License number is required.";
    else if (!isValidLicenseNumber(form.license)) {
      next.license = "Enter a valid driver license number.";
    }

    if (!form.license_creation_date) {
      next.license_creation_date = "License creation date is required.";
    } else if (form.license_creation_date > maxLicenseCreationDate) {
      next.license_creation_date = "License creation date must be at least 1 year before today.";
    }

    if (!form.expiry) next.expiry = "TR license expiry date is required.";
    else if (new Date(form.expiry) < new Date(new Date().toDateString())) {
      next.expiry = "TR license expiry must be today or in the future.";
    }

    if (!form.nt_expiry) next.nt_expiry = "NT license expiry date is required.";
    else if (new Date(form.nt_expiry) < new Date(new Date().toDateString())) {
      next.nt_expiry = "NT license expiry must be today or in the future.";
    }

    const aadDigits = digitsOnly(form.aadhaar);
    if (!form.aadhaar.trim()) next.aadhaar = "Aadhaar number is required.";
    else if (aadDigits.length !== 12) next.aadhaar = "Aadhaar must be 12 digits.";

    const { mobile_number } = parseMobile(form.phone);
    if (!form.phone.trim()) next.phone = "Mobile number is required.";
    else if (!INDIAN_MOBILE_PATTERN.test(mobile_number))
      next.phone = "Enter a valid 10-digit mobile number.";

    const ageNum = parseInt(form.age, 10);
    if (!form.age.toString().trim()) next.age = "Age is required.";
    else if (!Number.isFinite(ageNum) || ageNum < 18 || ageNum > 70) {
      next.age = "Age must be between 18 and 70.";
    }

    if (!form.mipl_location_id.trim())
      next.mipl_location_id = "MIPL location not found in your profile.";

    if (!profileImage) next.profileImage = "Profile image is required.";

    const missingProofs = ID_PROOF_SLOTS.filter(({ key }) => !idProofs[key]);
    if (missingProofs.length > 0) {
      next.documents = missingDocumentsMessage(missingProofs);
    }

    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildFormData = async () => {
    /** @type {Array<{ label: string; beforeBytes: number; afterBytes: number; kind: string }>} */
    const logs = [];
    const appendLog = (entry) => {
      logs.push(entry);
    };

    const fd = new FormData();
    const { country_code, mobile_number } = parseMobile(form.phone);

    fd.append("full_name", form.name.trim());
    fd.append("license_number", form.license.trim());
    fd.append("license_creation_date", form.license_creation_date);
    fd.append("license_expiry_date", form.expiry);
    fd.append("nt_license_expiry_date", form.nt_expiry);
    fd.append("adhar_card_number", normalizeAadhaarForApi(form.aadhaar));
    fd.append("mobile_number", mobile_number);
    fd.append("country_code", country_code);
    fd.append("age", String(parseInt(form.age, 10)));
    fd.append("mipl_location_id", form.mipl_location_id.trim());
    if (fingerprintSignature.trim()) {
      fd.append("fingerprint_signature", fingerprintSignature.trim());
    }

    const profileBefore = profileImage.size;
    const compressedProfile = await compressImageFile(
      profileImage,
      SUBMIT_COMPRESSION_OPTIONS
    );
    appendLog({
      label: "Profile photo",
      beforeBytes: profileBefore,
      afterBytes: compressedProfile.size,
      kind: "compressed",
    });

    const preparedFiles = [];
    const docMeta = [];

    for (const { key, label } of ID_PROOF_SLOTS) {
      const file = idProofs[key];
      if (!file) continue;
      const docLabel = `ID proof · ${label}`;
      let out = file;

      if (isImageForSubmitCompression(file)) {
        try {
          out = await compressImageFile(file, SUBMIT_COMPRESSION_OPTIONS);
          appendLog({
            label: docLabel,
            beforeBytes: file.size,
            afterBytes: out.size,
            kind: "compressed",
          });
        } catch (err) {
          console.error(err);
          appendLog({
            label: docLabel,
            beforeBytes: file.size,
            afterBytes: file.size,
            kind: "fallback",
          });
        }
      } else {
        appendLog({
          label: docLabel,
          beforeBytes: file.size,
          afterBytes: file.size,
          kind: "skipped",
        });
      }

      preparedFiles.push(out);
      docMeta.push({ document_type: key, file_name: out.name });
    }

    fd.append("document_data", JSON.stringify(docMeta));
    fd.append("profile_image", compressedProfile, compressedProfile.name);

    preparedFiles.forEach((file) => {
      fd.append("file", file, file.name);
    });

    return { fd, logs };
  };

  const performDriverSubmit = async () => {
    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    const url = `${ACCOUNTANT_API}${endpoint.addDriver}`;
    setLoading(true);
    setCompressionLogs(null);

    // Enroll the captured fingerprint first; the driver is only created if the
    // fingerprint API accepts it.
    const capturedFingerprint = fingerprintSignature.trim();
    if (capturedFingerprint) {
      const personId = getFingerprintExternalId();
      if (!personId) {
        toast.error("Aadhaar or mobile number is required to enroll the fingerprint.");
        setLoading(false);
        setSubmitStage("idle");
        return;
      }

      if (!fingerprintEnrolledRef.current) {
        setSubmitStage("enroll");
        try {
          await enrollFingerprint({
            personId,
            templateData: capturedFingerprint,
          });
          fingerprintEnrolledRef.current = true;
          enrolledExternalIdRef.current = personId;
        } catch (err) {
          console.error(err);
          const enrollMessage = err?.message?.trim() || "";
          toast.error(
            /duplicate biometric/i.test(enrollMessage)
              ? "This fingerprint is already enrolled. Driver was not added. Please rescan."
              : enrollMessage ||
                  "Fingerprint enrollment failed. Driver was not added."
          );
          fingerprintEnrolledRef.current = false;
          enrolledExternalIdRef.current = "";
          setFingerprintSignature("");
          setLoading(false);
          setSubmitStage("idle");
          return;
        }
      }
    }

    setSubmitStage("compress");

    let formBody;
    try {
      const { fd, logs } = await buildFormData();
      formBody = fd;
      setCompressionLogs(logs);

      console.groupCollapsed(
        "%c[Driver form] Image compression",
        "color:#2563eb;font-weight:bold"
      );
      logs.forEach((entry) => {
        console.log(formatCompressionLogLine(entry));
      });
      console.table(
        logs.map((e) => ({
          Label: e.label,
          Before: formatFileSize(e.beforeBytes),
          After: formatFileSize(e.afterBytes),
          Kind: e.kind,
        }))
      );
      console.groupEnd();
    } catch (err) {
      console.error(err);
      const msg =
        err && typeof err.message === "string" && err.message.trim()
          ? err.message
          : "Could not prepare images for upload.";
      toast.error(msg);
      setLoading(false);
      setSubmitStage("idle");
      return;
    }

    setSubmitStage("upload");

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formBody,
      });

      const payload = await parseBody(response);

      if (!response.ok) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      const apiCode = payload?.code;
      if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      driverCreatedRef.current = true;
      fingerprintEnrolledRef.current = false;
      enrolledExternalIdRef.current = "";
      toast.success(payload?.message || "Driver added successfully.");
      setTimeout(() => {
        navigate("/driver-management");
      }, 1500);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection and try again."
          : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
      setSubmitStage("idle");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    pendingDriverSubmitRef.current = true;
    const sent = await requestSendOtp();
    if (!sent) {
      pendingDriverSubmitRef.current = false;
      return;
    }

    setOtp(emptyOtpDigits());
    setTimeLeft(OTP_TIMEOUT_SEC);
    setShowOTP(true);
  };

  const requestSendOtp = async () => {
    const { country_code, mobile_number } = parseMobile(form.phone);
    if (!form.phone.trim() || !INDIAN_MOBILE_PATTERN.test(mobile_number)) {
      toast.error("Enter a valid 10-digit mobile number first.");
      return false;
    }
    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return false;
    }
    setOtpSendLoading(true);
    try {
      const response = await fetch(`${ACCOUNTANT_API}${endpoint.sendOtp}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mobile_number, country_code, type: form.type }),
      });
      const payload = await parseBody(response);
      if (!response.ok || (payload?.code != null && payload.code !== 200 && payload.code !== 201)) {
        toast.error(getErrorMessage(payload, response));
        return false;
      }
      const apiOtp = payload?.data?.otp ?? payload?.data?.OTP;
      if (apiOtp != null && String(apiOtp).trim() !== "") {
        setOtpFromResponse(String(apiOtp).trim());
      } else {
        setOtpFromResponse("");
      }
      toast.success(payload?.message || "OTP sent to your mobile number.");
      return true;
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : "Could not send OTP."
      );
      return false;
    } finally {
      setOtpSendLoading(false);
    }
  };

  const handleOTPChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < otp.length - 1) {
      otpRefs.current[index + 1]?.focus();
    }

    if (!value && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const otpExpired = timeLeft === 0;

  const verifyOTP = async () => {
    if (timeLeft === 0) {
      toast.error("OTP expired. Please resend OTP and try again.");
      return;
    }
    const entered = otp.join("");
    if (entered.length !== OTP_LEN) {
      toast.error(`Enter the ${OTP_LEN}-digit OTP.`);
      return;
    }
    const { country_code, mobile_number } = parseMobile(form.phone);
    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }
    setOtpVerifyLoading(true);
    try {
      const response = await fetch(`${ACCOUNTANT_API}${endpoint.otpVerify}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mobile_number,
          country_code,
          otp: entered,
          type: form.type
        }),
      });
      const payload = await parseBody(response);
      if (!response.ok || (payload?.code != null && payload.code !== 200 && payload.code !== 201)) {
        toast.error(getErrorMessage(payload, response));
        return;
      }
      toast.success(payload?.message || "OTP verified.");
      const runDriverSubmit = pendingDriverSubmitRef.current;
      pendingDriverSubmitRef.current = false;
      setShowOTP(false);
      setOtpFromResponse("");
      if (runDriverSubmit) {
        await performDriverSubmit();
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : "Could not verify OTP."
      );
    } finally {
      setOtpVerifyLoading(false);
    }
  };

  const resendOTP = async () => {
    const ok = await requestSendOtp();
    if (!ok) return;
    setTimeLeft(OTP_TIMEOUT_SEC);
    setOtp(emptyOtpDigits());
  };

  return (
    <>
    <div className='col-md-4'>
      <Link
        to="/driver-management"
        onClick={handleBackToDriverList}
        className="btn btn-primary shrink-0 mt-4"
      >
        Back
      </Link>
    </div>
      <form
        className="p-6 mx-auto bg-white shadow-sm mt-7 font-UbuntuRegular [&_.form-control]:h-12"
        onSubmit={handleSubmit}
        noValidate
      >
        <p className="mb-2 text-center text-sm font-UbuntuMedium text-gray-700">
          Profile photo<span className="text-danger"> *</span>
        </p>
        <div className="flex justify-center mb-6">
          <CameraCapture
            variant="circle"
            modalTitle="Take profile photo"
            fileNamePrefix="profile"
            value={profileImage}
            onCapture={handleProfileCapture}
            disabled={loading}
          />
        </div>
        {fieldErrors.profileImage ? (
          <p className="text-danger text-sm text-center mb-4">{fieldErrors.profileImage}</p>
        ) : null}

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <FieldLabel htmlFor="driver-full-name">Full name</FieldLabel>
            <input
              id="driver-full-name"
              name="name"
              placeholder="Enter full name"
              className="form-control"
              value={form.name}
              maxLength={FULL_NAME_MAX_LENGTH}
              onChange={handleChange}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData("text");
                setForm((prev) => ({
                  ...prev,
                  name: normalizeFullNameInput(pasted),
                }));
                if (fieldErrors.name) clearFieldError("name");
              }}
              disabled={loading}
            />
            {fieldErrors.name ? (
              <p className="text-danger small mt-1 mb-0">{fieldErrors.name}</p>
            ) : null}
          </div>
          <div>
            <FieldLabel htmlFor="driver-license">Driver license number</FieldLabel>
            <input
              id="driver-license"
              name="license"
              placeholder="Driver license number"
              className="form-control"
              value={form.license}
              maxLength={LICENSE_MAX_LENGTH}
              onChange={handleChange}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData("text");
                setForm((prev) => ({
                  ...prev,
                  license: normalizeLicenseInput(pasted),
                }));
                if (fieldErrors.license) clearFieldError("license");
              }}
              disabled={loading}
              autoComplete="off"
              inputMode="text"
            />
            {fieldErrors.license ? (
              <p className="text-danger small mt-1 mb-0">{fieldErrors.license}</p>
            ) : null}
          </div>
          <div>
            <FieldLabel htmlFor="driver-license-creation">License creation date</FieldLabel>
            <SingleDatePicker
              id="driver-license-creation"
              value={form.license_creation_date}
              maxDate={maxLicenseCreationDate}
              placeholder="Select license creation date"
              disabled={loading}
              error={fieldErrors.license_creation_date || ""}
              triggerIcon={datePickerTriggerIcon}
              onChange={(nextDate) => {
                setForm((prev) => ({ ...prev, license_creation_date: nextDate }));
                if (fieldErrors.license_creation_date) clearFieldError("license_creation_date");
              }}
            />
          </div>
          <div>
            <FieldLabel htmlFor="driver-license-expiry">TR License Expiry Date</FieldLabel>
            <SingleDatePicker
              id="driver-license-expiry"
              value={form.expiry}
              minDate={getTodayDateString()}
              placeholder="Select TR license expiry date"
              disabled={loading}
              error={fieldErrors.expiry || ""}
              triggerIcon={datePickerTriggerIcon}
              onChange={(nextDate) => {
                setForm((prev) => ({ ...prev, expiry: nextDate }));
                if (fieldErrors.expiry) clearFieldError("expiry");
              }}
            />
          </div>
          <div>
            <FieldLabel htmlFor="driver-nt-license-expiry">NT License Expiry Date</FieldLabel>
            <SingleDatePicker
              id="driver-nt-license-expiry"
              value={form.nt_expiry}
              minDate={getTodayDateString()}
              placeholder="Select NT license expiry date"
              disabled={loading}
              error={fieldErrors.nt_expiry || ""}
              triggerIcon={datePickerTriggerIcon}
              onChange={(nextDate) => {
                setForm((prev) => ({ ...prev, nt_expiry: nextDate }));
                if (fieldErrors.nt_expiry) clearFieldError("nt_expiry");
              }}
            />
          </div>

          <div>
            <FieldLabel htmlFor="driver-aadhaar">Aadhaar card number</FieldLabel>
            <input
              id="driver-aadhaar"
              name="aadhaar"
              placeholder="Enter 12-digit Aadhaar number"
              className="form-control"
              value={form.aadhaar}
              onChange={handleChange}
              disabled={loading}
              inputMode="numeric"
              autoComplete="off"
              maxLength={14}
              title="Enter 12 digits Aadhaar number"
            />
            {fieldErrors.aadhaar ? (
              <p className="text-danger small mt-1 mb-0">{fieldErrors.aadhaar}</p>
            ) : null}
          </div>

          <div>
            <FieldLabel htmlFor="driver-phone">Mobile number</FieldLabel>
            <div className="relative flex flex-1 min-w-0">
              <div className="flex w-full min-w-0 rounded-lg border border-gray-300 overflow-hidden bg-white focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary">
                <span
                  className="flex items-center shrink-0 px-3 bg-gray-100 border-r border-gray-300 text-gray-800 text-sm font-medium select-none"
                  aria-hidden="true"
                >
                  +91
                </span>
                <input
                  id="driver-phone"
                  name="phone"
                  placeholder="10-digit number"
                  className="form-control flex-1 min-w-0 rounded-none border-0 shadow-none focus:ring-0 h-12 pr-3"
                  value={form.phone}
                  onChange={handleChange}
                  disabled={loading}
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={10}
                  pattern="\d{10}"
                  title="Enter exactly 10 digits after +91"
                />
              </div>
            </div>
            <p className="text-gray-500 small mt-1 mb-0">
              You will receive an OTP when you submit this form to confirm this number.
            </p>
            {fieldErrors.phone ? (
              <p className="text-danger small mt-1 mb-0">{fieldErrors.phone}</p>
            ) : null}
          </div>

          <div>
            <FieldLabel htmlFor="driver-age">Age</FieldLabel>
            <input
              id="driver-age"
              name="age"
              placeholder="Enter age"
              className="form-control"
              value={form.age}
              onChange={handleChange}
              disabled={loading}
              inputMode="numeric"
              maxLength={2}
              pattern="[0-9]*"
              title="Numbers only, up to 3 digits"
            />
            {fieldErrors.age ? (
              <p className="text-danger small mt-1 mb-0">{fieldErrors.age}</p>
            ) : null}
          </div>

        </div>

        <div className="mb-6 flex items-start gap-x-4 flex-wrap">
          <div className="w-full">
            <p className="!text-primary font-UbuntuMedium mb-3">
              Upload ID proofs (JPG or PNG)
            </p>
            <div className="flex gap-4 flex-wrap items-start">
              {ID_PROOF_SLOTS.map(({ key, label }) => {
                const file = idProofs[key];
                const previewSrc = idProofPreviewUrls[key];

                return (
                  <div key={key} className="flex flex-col gap-2">
                    <span className="text-sm font-UbuntuMedium text-gray-800">
                      {label}
                      <span className="text-danger"> *</span>
                    </span>
                    {file && previewSrc ? (
                      <div className="group relative w-[168px] overflow-hidden rounded-xl border border-gray-200/90 bg-white shadow-md ring-1 ring-black/5 transition hover:shadow-lg hover:ring-black/10 sm:w-[188px]">
                        <button
                          type="button"
                          aria-label={`Remove ${label}`}
                          className="absolute right-2 top-2 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-base leading-none text-gray-500 shadow-md backdrop-blur-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() => removeIdProof(key)}
                          disabled={loading}
                        >
                          ×
                        </button>
                        <div className="relative aspect-[4/5] w-full bg-gray-100">
                          <img
                            src={previewSrc}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent px-2 pb-2 pt-10">
                            <p className="truncate text-center text-[11px] font-medium text-white drop-shadow-sm">
                              {file.name}
                            </p>
                            <p className="text-center text-[10px] text-white/85">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <label className="flex h-32 w-60 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed px-2 text-center">
                        <span className="flex items-center text-sm">
                          <img src={UploadIcon} className="mr-2 shrink-0" alt="" />
                          Upload
                        </span>
                        <input
                          type="file"
                          hidden
                          accept="image/png,image/jpeg"
                          onChange={handleIdProofUpload(key)}
                          disabled={loading}
                        />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
            {fieldErrors.documents ? (
              <p className="text-danger small mt-2 mb-0">{fieldErrors.documents}</p>
            ) : null}
          </div>
          <FingerprintCapture
            value={fingerprintSignature}
            onCapture={(result) => {
              void discardEnrolledFingerprint();
              setFingerprintSignature(
                (result.templateData || result.bitmapData || "").trim()
              );
              clearFieldError("fingerprint");
            }}
            onClear={() => {
              void discardEnrolledFingerprint();
              setFingerprintSignature("");
            }}
            disabled={loading || !canVerifyFingerprint}
            hint={
              canVerifyFingerprint || fingerprintSignature
                ? ""
                : "Fill all fields above before verifying the fingerprint."
            }
            error={fieldErrors.fingerprint || ""}
          />
        </div>

        {/* {compressionLogs?.length ? (
          <div
            className="mt-4 rounded-lg border border-emerald-200/80 bg-emerald-50/90 px-4 py-3 text-sm shadow-sm"
            role="status"
            aria-live="polite"
          >
            <p className="font-UbuntuMedium text-gray-900 mb-2">Compression log</p>
            <ul className="space-y-1.5 text-gray-800 text-xs leading-relaxed m-0 pl-4 list-disc">
              {compressionLogs.map((entry, i) => (
                <li key={`${entry.label}-${i}`}>{formatCompressionLogLine(entry)}</li>
              ))}
            </ul>
          </div>
        ) : null} */}

        <div className="flex gap-2 mt-4">
          <Link
            to="/driver-management"
            className="z-10 px-4 border-[1px] !border-primary !bg-primary !py-3 !rounded-full text-[15px] md:min-w-32 text-center text-white transition-all"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading || showOTP || otpSendLoading || profileLocationLoading}
            className="z-10 border-[2px] !border-danger !bg-danger px-4 !py-3 md:py-[10px] rounded-full text-center text-[15px] md:min-w-32 text-white disabled:opacity-60"
          >
            {loading && submitStage === "enroll"
              ? "Enrolling fingerprint…"
              : loading && submitStage === "compress"
              ? "Compressing…"
              : loading && submitStage === "upload"
                ? "Uploading…"
                : loading
                  ? "Adding…"
                  : otpSendLoading
                    ? "Sending OTP…"
                    : "Add"}
          </button>
        </div>
      </form>

      <Toastify />

      {showOTP && (
        <div className="fixed inset-0 bg-black/50 bg-opacity-70 flex justify-center items-center z-[9999]">
          <div className="bg-white p-6 w-full max-w-md text-center font-UbuntuRegular">
            <h2 className="text-lg font-semibold mb-2">Confirm with OTP</h2>
            <p className="text-sm mb-4">
              Enter the {OTP_LEN}-digit OTP sent to <b>+91 {form.phone}</b>. After
              verification, your driver registration will be submitted.
            </p>

            {otpFromResponse ? (
              <div className="mb-4 rounded-lg border border-primary/30 bg-primary/5 px-3 py-3 text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-600">
                  OTP from response
                </p>
                <p
                  className="mt-1 font-mono text-2xl font-bold tracking-[0.35em] text-primary"
                  aria-live="polite"
                >
                  {otpFromResponse}
                </p>
              </div>
            ) : null}

            <div className="flex justify-center gap-1.5 sm:gap-2 mb-3 flex-wrap">
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    otpRefs.current[i] = el;
                  }}
                  maxLength={1}
                  inputMode="numeric"
                  className="w-10 h-11 sm:w-12 sm:h-12 border text-center form-control"
                  value={digit}
                  disabled={otpVerifyLoading || otpExpired}
                  onChange={(e) => handleOTPChange(e.target.value, i)}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !otp[i] && i > 0) {
                      otpRefs.current[i - 1]?.focus();
                    }
                  }}
                />
              ))}
            </div>

            <p className={`text-sm mb-3 ${otpExpired ? "text-danger" : ""}`}>
              Remaining Time:{" "}
              <b>
                {String(Math.floor(timeLeft / 60)).padStart(2, "0")}:
                {String(timeLeft % 60).padStart(2, "0")}s
              </b>
            </p>
            {otpExpired ? (
              <p className="text-sm text-danger mb-3">
                OTP expired. Cancel or resend OTP to continue.
              </p>
            ) : null}

            <button
              type="button"
              onClick={() => verifyOTP()}
              disabled={otpVerifyLoading || otpSendLoading || otpExpired}
              className="btn !bg-danger border-danger text-white w-full !py-3 mb-2 !rounded-full disabled:opacity-60"
            >
              {otpVerifyLoading ? "Verifying…" : "Verify"}
            </button>

            <button
              type="button"
              onClick={() => closeOtpModal()}
              disabled={otpVerifyLoading}
              className="btn border !bg-primary w-full !py-3 mb-2 !rounded-full disabled:opacity-60"
            >
              Cancel
            </button>

            <p className="text-sm">
              Didn’t get code?{" "}
              <button
                type="button"
                className="underline text-primary bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50"
                onClick={() => resendOTP()}
                disabled={otpSendLoading || otpVerifyLoading || timeLeft > 0}
              >
                Resend
              </button>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AddNewDriver;
